import {Button} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import React from 'react';
import {toast} from 'react-toastify';

import {
  SlurmCommandType,
  useSubmitSlurmCommandMutation,
} from '../../generated/graphql';
import {
  extractErrorMessageFromApolloError,
  extractStatusCodeFromApolloError,
  propagateErrorsContext,
} from '../../util/errors';
import {SlurmHealth} from './Slurm-Health';

const VARIANT_CONFIG = {
  cancel: {
    title: 'Cancel this Slurm job?',
    description: (jobId: string, _isRunLive: boolean) =>
      `Stopping this Slurm job, "${jobId}" will terminate all active processes and any runs currently in progress. This action cannot be undone.`,
    confirmLabel: 'Cancel Slurm job',
    toastMessage: (jobId: string) => `Cancel requested for ${jobId}`,
    command: SlurmCommandType.Cancel,
  },
  requeue: {
    title: 'Requeue this Slurm job?',
    description: (jobId: string, isRunLive: boolean) =>
      isRunLive
        ? `This Slurm job, "${jobId}" will terminate all active processes and any runs currently in progress, then be added back to the queue to run again from the beginning. Any progress from the current execution will not be saved.`
        : `This Slurm job, "${jobId}", is no longer running. Slurm will try to add the existing job back to the queue to run again from the beginning. This only works while Slurm still has the job record; if it has aged out, the cluster's MinJobAge setting may need to be increased.`,
    confirmLabel: 'Requeue Slurm job',
    toastMessage: (jobId: string) => `Requeue requested for ${jobId}`,
    command: SlurmCommandType.Requeue,
  },
} as const;

export type SlurmConfirmVariant = keyof typeof VARIANT_CONFIG;

interface SlurmJobConfirmModalProps {
  isOpen: boolean;
  jobId: string;
  entityName: string;
  projectName: string;
  runName: string;
  variant: SlurmConfirmVariant;
  isRunLive: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const SlurmJobConfirmModal: React.FC<SlurmJobConfirmModalProps> =
  React.memo(
    ({
      isOpen,
      jobId,
      entityName,
      projectName,
      runName,
      variant,
      isRunLive,
      onClose,
      onSuccess,
    }) => {
      const config = VARIANT_CONFIG[variant];
      const [submitSlurmCommand] = useSubmitSlurmCommandMutation({
        context: propagateErrorsContext(),
      });

      const handleConfirm = () => {
        submitSlurmCommand({
          variables: {
            entityName,
            projectName,
            runName,
            command: config.command,
          },
        })
          .then(() => {
            toast(config.toastMessage(jobId));
            onSuccess?.();
          })
          .catch((error: unknown) => {
            if (extractStatusCodeFromApolloError(error) === 409) {
              toast(
                `A ${variant} is already in flight for ${jobId}. Give it a moment and try again.`,
                {type: 'info'}
              );
              return;
            }
            const serverMessage = extractErrorMessageFromApolloError(error);
            toast(serverMessage ?? `Failed to ${variant} Slurm job ${jobId}`, {
              type: 'error',
            });
          });
        onClose();
      };

      return (
        <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose()}>
          <Dialog.Portal>
            <Dialog.Overlay />
            <Dialog.Content
              className="w-[540px] rounded-lg"
              onInteractOutside={e => {
                e.preventDefault();
              }}>
              <Dialog.Title>{config.title}</Dialog.Title>
              <Dialog.Description className="mt-16">
                {config.description(jobId, isRunLive)}
                <SlurmHealth
                  projectName={projectName}
                  entityName={entityName}
                  runName={runName}
                  skip={!isOpen}
                />
              </Dialog.Description>
              <div className="mb-8 mt-24 flex items-center">
                <Button
                  variant="destructive"
                  size="large"
                  onClick={handleConfirm}>
                  {config.confirmLabel}
                </Button>
                <Dialog.Close className="ml-8" asChild>
                  <Button variant="ghost" size="large">
                    Cancel
                  </Button>
                </Dialog.Close>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      );
    }
  );
